import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/sl';
import { useEffect, useState } from 'react';
import { EventAPI } from '../../../network';
import { EventAdminModel } from '../../../network/rest/models/EventAdminModel';
import ApiErrorHandler from '../../../utils/ApiErrorHandler';
import { translations } from '../../../utils/staticContent';
import ImageUploadForm from '../../components/ImageUploadForm';

export default function EventForm({ event }: { event?: EventAdminModel }) {
    const [title, setTitle] = useState(event?.title || '');
    const [description, setDescription] = useState('');
    const [link, setLink] = useState(event?.link || '');
    const [date, setDate] = useState<Dayjs | null>(event?.date ? dayjs(event.date) : null);
    const [dateError, setDateError] = useState(false);
    const [error, setError] = useState('');
    const [fileId, setFileId] = useState('');

    useEffect(() => {
        if (event) {
            getData(event.id);
        }
    }, []);

    async function getData(id: string) {
        try {
            const response = await EventAPI.getEventAdminApiV1EventsAdminEventGet({
                id,
            });
            if (response) {
                setTitle(response.title);
                setDescription(response.description);
                setLink(response.link);
                setDate(dayjs(response.date));
            }
        } catch (e) {
            ApiErrorHandler(e);
        }
    }

    return (
        <div className="EventForm">
            <Container component="main" maxWidth="md">
                <Grid
                    container
                    component="form"
                    rowSpacing={2}
                    columnSpacing={{ xs: 1, sm: 2 }}
                    noValidate
                    mt={1}
                    onSubmit={async (e) => {
                        e.preventDefault();
                        if (!date) {
                            return;
                        }
                        try {
                            const formattedDate = new Date(date.toDate());

                            if (event) {
                                await EventAPI.eventUpdateApiV1EventsEventPut({
                                    id: event.id,
                                    eventUpdateModel: {
                                        title,
                                        description,
                                        link,
                                        date: formattedDate,
                                    },
                                });
                            } else {
                                await EventAPI.createEventApiV1EventsEventPost({
                                    eventCreateModel: {
                                        title,
                                        description,
                                        link,
                                        date: formattedDate,
                                        fileId,
                                    },
                                });
                                setDate(null);
                                setTitle('');
                                setDescription('');
                                setLink('');
                            }
                        } catch (e) {
                            const error = ApiErrorHandler(e);
                            setError(error);
                        }
                    }}
                >
                    <Grid item xs={12} sm={6}>
                        <TextField
                            required
                            fullWidth
                            id="title"
                            label={translations.title}
                            name="title"
                            autoFocus
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="sl">
                            <DateTimePicker
                                label={translations.dateRequired}
                                onChange={(newValue) => {
                                    setDate(newValue);
                                }}
                                disablePast={event ? false : true}
                                format="DD/MM/YYYY hh:mm"
                                slotProps={{ textField: { fullWidth: true } }}
                                onError={(e) => (e ? setDateError(true) : setDateError(false))}
                                value={date}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <TextField
                            required
                            fullWidth
                            name="link"
                            label={translations.url}
                            id="link"
                            value={link}
                            onChange={(e) => setLink(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <TextField
                            required
                            fullWidth
                            name="description"
                            label={translations.description}
                            id="description"
                            value={description}
                            multiline={true}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </Grid>
                    <ImageUploadForm setError={setError} fileId={fileId} setFileId={setFileId} />
                    <Grid item xs={12} sm={12}>
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, mb: 2 }}
                            disabled={
                                title.length < 2 ||
                                title.length > 30 ||
                                description.length < 2 ||
                                description.length > 500 ||
                                !date ||
                                dateError
                            }
                        >
                            {translations.save}
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <Typography component="span" color="red">
                            {error}
                        </Typography>
                    </Grid>
                </Grid>
            </Container>
        </div>
    );
}
